import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Stack
} from '@mui/material';
import { endOfMonth, format, isValid, parseISO, startOfMonth } from 'date-fns';

import { Budget, useBudgets } from '../hooks/useBudgets';
import { useBudgetVsActual } from '../hooks/useReports';
import { calculateBudgetUsage } from '../utils/budgetUtils';

import BudgetSummaryCard from '../components/budget/BudgetSummaryCard';
import EditBudgetModal from '../components/budget/EditBudgetModal';
import FloatingActionButton from '../components/common/FloatingActionButton';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import { useFormatting } from '../context/FormattingContext';
import { formatMoney } from '../utils/formatting';
import { authFetch } from '../lib/authFetch';

const normalizeBudgetCategory = (category: string) => category.trim().toLowerCase();
const isAllCategoriesBudget = (category?: string): boolean => {
    const normalized = normalizeBudgetCategory(category || '');
    return (
        !normalized ||
        normalized === 'all' ||
        normalized === 'all categories' ||
        normalized === 'all expenses' ||
        normalized === 'total' ||
        normalized === 'overall'
    );
};

const parseDateOnlyValue = (value?: string): Date | null => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    return isValid(parsed) ? parsed : null;
};

const parseMonthWindow = (monthKey: string): { start: Date; end: Date } | null => {
    if (!/^\d{4}-\d{2}$/.test(monthKey)) return null;
    const parsed = parseISO(`${monthKey}-01`);
    if (!isValid(parsed)) return null;
    return {
        start: startOfMonth(parsed),
        end: endOfMonth(parsed),
    };
};

const BudgetPage: React.FC = () => {
    const formatting = useFormatting();

    useEffect(() => {
        document.title = 'My Budget | Velora Money';
    }, []);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const [deletingBudgetId, setDeletingBudgetId] = useState<string | null>(null);
    const [deletingBudgetCategory, setDeletingBudgetCategory] = useState<string>('');

    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const { budgets } = useBudgets(refreshKey);
    const rollupRange = useMemo(() => {
        const fallback = {
            start: startOfMonth(new Date()),
            end: endOfMonth(new Date()),
        };
        if (!budgets.length) return fallback;

        const startDates = budgets
            .map((budget) => parseDateOnlyValue(budget.startDate))
            .filter((date): date is Date => Boolean(date));
        const endDates = budgets
            .map((budget) => parseDateOnlyValue(budget.endDate))
            .filter((date): date is Date => Boolean(date));

        if (!startDates.length || !endDates.length) return fallback;

        const start = startDates.reduce((minDate, current) =>
            current.getTime() < minDate.getTime() ? current : minDate
        );
        const end = endDates.reduce((maxDate, current) =>
            current.getTime() > maxDate.getTime() ? current : maxDate
        );

        return { start, end };
    }, [budgets]);

    const {
        data: budgetVsActual,
        isLoading: actualsLoading,
        isError: hasActualsError,
    } = useBudgetVsActual({
        startDate: format(rollupRange.start, 'yyyy-MM-dd'),
        endDate: format(rollupRange.end, 'yyyy-MM-dd'),
    });

    const actualsByBudgetId = useMemo(() => {
        const totals = new Map<string, number>();
        const series = budgetVsActual?.series || [];

        for (const budget of budgets) {
            const budgetStart = parseDateOnlyValue(budget.startDate);
            if (!budgetStart) {
                totals.set(budget._id, 0);
                continue;
            }

            const parsedEnd = parseDateOnlyValue(budget.endDate);
            const budgetEnd = parsedEnd || rollupRange.end;
            const budgetCategory = normalizeBudgetCategory(budget.category);
            const isAllBudget = isAllCategoriesBudget(budget.category);

            let totalSpent = 0;
            for (const row of series) {
                const monthWindow = parseMonthWindow(row.month);
                if (!monthWindow) continue;
                if (monthWindow.start > budgetEnd || monthWindow.end < budgetStart) continue;

                const byCategory = row.byCategoryActual || {};

                if (isAllBudget) {
                    const rowTotal = Object.values(byCategory).reduce((sum, value) => {
                        const amount = Number(value);
                        return sum + (Number.isFinite(amount) ? amount : 0);
                    }, 0);
                    totalSpent += rowTotal;
                    continue;
                }

                const rowCatMap = new Map<string, number>();
                for (const [category, value] of Object.entries(byCategory)) {
                    const amount = Number(value);
                    rowCatMap.set(normalizeBudgetCategory(category), Number.isFinite(amount) ? amount : 0);
                }
                totalSpent += rowCatMap.get(budgetCategory) || 0;
            }

            totals.set(budget._id, Number.isFinite(totalSpent) ? totalSpent : 0);
        }

        return totals;
    }, [budgetVsActual, budgets, rollupRange.end]);

    const handleRefresh = () => setRefreshKey((prev) => prev + 1);

    const handleEdit = (budget: Budget, e: React.MouseEvent<HTMLButtonElement>) => {
        triggerRef.current = e.currentTarget;
        setEditingBudget(budget);
        setModalOpen(true);
    };

    const handleAdd = () => {
        const fab = document.getElementById('fab-add-budget') as HTMLButtonElement | null;
        triggerRef.current = fab;
        setEditingBudget(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        triggerRef.current?.focus();
    };

    const handleSuccessModal = () => {
        setModalOpen(false);
        handleRefresh();
        triggerRef.current?.focus();
    };

    const handleDelete = (budgetId: string, category: string) => {
        setDeletingBudgetId(budgetId);
        setDeletingBudgetCategory(category);
    };

    const handleConfirmDeleteBudget = async () => {
        if (!deletingBudgetId) return;

        try {
            const res = await authFetch(`/api/budgets/${deletingBudgetId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (res.ok) {
                handleRefresh();
            } else {
                const err = await res.json();
                alert(err.message || 'Failed to delete budget.');
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('Error deleting budget.');
        } finally {
            setDeletingBudgetId(null);
            setDeletingBudgetCategory('');
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
            <Stack spacing={{ xs: 2.5, md: 3 }}>
                <Paper
                    sx={{
                        p: { xs: 2.5, sm: 3, md: 4 },
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
                        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
                    }}
                >
                    <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 999,
                                bgcolor: 'rgba(25, 118, 210, 0.10)',
                                color: 'primary.main',
                                width: 'fit-content',
                            }}
                        >
                            <Typography variant="body2" fontWeight={700}>
                                Budget workspace
                            </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
                            📊 Budgets
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                            Track and manage your spending limits across categories.
                        </Typography>
                    </Stack>
                </Paper>

                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 2, sm: 2.5, md: 3 },
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
                    }}
                >
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                                Overview
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                                Budget Status
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 860 }}>
                                Review planned budgets against actual spend rollups and keep your category limits current.
                            </Typography>
                        </Box>

                        {actualsLoading && (
                            <Typography variant="body2" color="text.secondary">
                                Loading actual spend from ledger rollups...
                            </Typography>
                        )}
                        {hasActualsError && (
                            <Typography variant="body2" color="error">
                                Could not load actual spend rollups. Planned budgets are shown, actuals default to {formatMoney(0, formatting)}.
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 2, sm: 2.5, md: 3 },
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
                    }}
                >
                    <Stack spacing={{ xs: 2, sm: 2.5 }}>
                        <Box>
                            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                                Budgets
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.25, mb: 0.75, fontWeight: 700 }}>
                                Budget List
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Edit or remove existing budgets while keeping each category&apos;s plan visible at a glance.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'repeat(2, minmax(280px, 1fr))',
                                    xl: 'repeat(3, minmax(280px, 1fr))',
                                },
                                gap: 2,
                            }}
                        >
                            {budgets.map((budget: Budget) => {
                                const actualSpent = actualsByBudgetId.get(budget._id) || 0;
                                const usage = calculateBudgetUsage(budget.amount, actualSpent);

                                return (
                                    <Paper
                                        key={budget._id}
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            borderRadius: 3,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <BudgetSummaryCard
                                            category={budget.category}
                                            usage={usage}
                                            totalBudget={budget.amount}
                                            startDate={new Date(budget.startDate)}
                                            endDate={budget.endDate ? new Date(budget.endDate) : undefined}
                                        />

                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={(e) => handleEdit(budget, e)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(budget._id, budget.category)}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {budgets.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No budgets yet. Use the add action to create your first budget.
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                <DeleteConfirmModal
                    open={!!deletingBudgetId}
                    onClose={() => {
                        setDeletingBudgetId(null);
                        setDeletingBudgetCategory('');
                    }}
                    onConfirm={handleConfirmDeleteBudget}
                    title="Delete Budget?"
                    description={`Are you sure you want to delete the budget for "${deletingBudgetCategory}"? This action cannot be undone.`}
                />

                <FloatingActionButton onClick={handleAdd} id="fab-add-budget" />

                <EditBudgetModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSuccess={handleSuccessModal}
                    budget={editingBudget}
                />
            </Stack>
        </Container>
    );
};

export default BudgetPage;
